/* eslint-disable max-lines */
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import ReactDatetime from 'react-datetime';
import { toast } from 'react-toastify';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';
import CommonHeader from '../../../components/Headers/CommonHeader';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import TaskBoardService from '../../../services/TaskBoard';
import './task-board.scss';

type TicketType = 'supplies' | 'one_on_one' | 'labels' | 'monthly_curriculum_event' | 'request';
type LabelTypeKey = 'cubby_label' | 'crib_label' | 'circle_time_board_stars' | 'toy_labels' | 'other';
type TicketStatus = 'pending' | 'completed' | 'cancelled';

const LABEL_TYPES: { key: LabelTypeKey; label: string }[] = [
  { key: 'cubby_label', label: 'Cubby Label' },
  { key: 'crib_label', label: 'Crib Label' },
  { key: 'circle_time_board_stars', label: 'Circle Time Board Stars' },
  { key: 'toy_labels', label: 'Toy Labels' },
  { key: 'other', label: 'Other:' },
];
type FilterTab = 'all' | TicketStatus;

type MyTicket = {
  id: number;
  ticketNumber: string;
  date: string;
  classroomNumber: string;
  ticketType: string;
  status: TicketStatus;
  dueDate: string | null;
  suppliesData: { whatNeed?: string; howMuchNeed?: string; whatFor?: string; needByDate?: string } | null;
  oneOnOneData: { notes?: string; meetingWith?: string } | null;
  monthlyExtrasData: { month?: string; activities?: string } | null;
  requestText: string | null;
};

const todayUs = () => moment().format('MM/DD/YYYY');

const todayIso = () => moment().format('YYYY-MM-DD');

const isoToUs = (iso: string | null) => {
  if (!iso) return '—';
  const m = moment(iso.slice(0, 10), 'YYYY-MM-DD');
  return m.isValid() ? m.format('MM/DD/YYYY') : iso;
};

const ticketTypeLabel: Record<string, string> = {
  supplies: 'Supplies',
  one_on_one: 'Meeting',
  labels: 'Labels',
  monthly_curriculum_event: 'Monthly Event',
  request: 'Request',
};

function getItemRequested(ticket: MyTicket): string {
  if (ticket.ticketType === 'supplies') return ticket.suppliesData?.whatNeed || '—';
  if (ticket.ticketType === 'one_on_one') return ticket.oneOnOneData?.notes?.slice(0, 40) || '—';
  if (ticket.ticketType === 'monthly_curriculum_event') return ticket.monthlyExtrasData?.month || '—';
  if (ticket.ticketType === 'request') return ticket.requestText?.slice(0, 40) || '—';
  if (ticket.ticketType === 'labels') return ticket.requestText?.slice(0, 40) || '—';
  return '—';
}

function getQuantity(ticket: MyTicket): string {
  if (ticket.ticketType === 'supplies') return ticket.suppliesData?.howMuchNeed || '—';
  return '—';
}

const STATUS_COLOR: Record<TicketStatus, string> = {
  pending: 'warning',
  completed: 'success',
  cancelled: 'secondary',
};

const TeacherTicketPage = () => {
  const { user, token, currentSchool }: any = useContext(AuthContext);

  const teacherName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();

  const [view, setView] = useState<'list' | 'form'>('list');
  const [filterTab, setFilterTab] = useState<FilterTab>('all');

  const [tickets, setTickets] = useState<MyTicket[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);

  const loadTickets = useCallback(async () => {
    const schoolId = user?.employee?.schoolId || currentSchool?.value;
    if (!schoolId) return;
    setLoadingTickets(true);
    try {
      const data = await TaskBoardService(token).listMyTeacherTickets(String(schoolId));
      setTickets(data || []);
    } catch {
      toast.error('Could not load tickets.');
    } finally {
      setLoadingTickets(false);
    }
  }, [token, user, currentSchool]);

  useEffect(() => {
    void loadTickets();
  }, [loadTickets]);

  const filteredTickets = useMemo(() => {
    if (filterTab === 'all') return tickets;
    return tickets.filter((t) => t.status === filterTab);
  }, [tickets, filterTab]);

  const [saving, setSaving] = useState(false);
  const [classroomNumber, setClassroomNumber] = useState('');
  const [ticketType, setTicketType] = useState<TicketType>('supplies');
  const [whatNeed, setWhatNeed] = useState('');
  const [howMuchNeed, setHowMuchNeed] = useState('');
  const [whatFor, setWhatFor] = useState('');
  const [needByDate, setNeedByDate] = useState('');
  const [oneOnOneNotes, setOneOnOneNotes] = useState('');
  const [eventMonth, setEventMonth] = useState('');
  const [activities, setActivities] = useState('');
  const [requestText, setRequestText] = useState('');
  const [selectedLabelTypes, setSelectedLabelTypes] = useState<LabelTypeKey[]>([]);
  const [labelOtherText, setLabelOtherText] = useState('');
  const [labelNamesText, setLabelNamesText] = useState('');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [datePickerStyle, setDatePickerStyle] = useState<React.CSSProperties>({});
  const dateInputRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setClassroomNumber('');
    setTicketType('supplies');
    setWhatNeed('');
    setHowMuchNeed('');
    setWhatFor('');
    setNeedByDate('');
    setOneOnOneNotes('');
    setEventMonth('');
    setActivities('');
    setRequestText('');
    setSelectedLabelTypes([]);
    setLabelOtherText('');
    setLabelNamesText('');
  };

  const canSubmit = useMemo(() => {
    if (!(teacherName.length > 0 && classroomNumber.trim().length > 0)) return false;
    if (ticketType !== 'labels') return true;
    return selectedLabelTypes.length > 0 && labelNamesText.trim().length > 0;
  }, [teacherName, classroomNumber, ticketType, selectedLabelTypes, labelNamesText]);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const schoolId = user?.employee?.schoolId || currentSchool?.value;
    if (!schoolId) {
      toast.error('School not found for this user.');
      return;
    }
    setSaving(true);
    try {
      await TaskBoardService(token).createTeacherTicket(schoolId, {
        teacherName,
        date: todayIso(),
        classroomNumber: classroomNumber.trim(),
        ticketType,
        suppliesData:
          ticketType === 'supplies'
            ? {
                whatNeed: whatNeed.trim(),
                howMuchNeed: howMuchNeed.trim(),
                whatFor: whatFor.trim(),
                needByDate: needByDate || undefined,
              }
            : undefined,
        oneOnOneData: ticketType === 'one_on_one' ? { notes: oneOnOneNotes.trim() } : undefined,
        monthlyExtrasData:
          ticketType === 'monthly_curriculum_event'
            ? { month: eventMonth.trim(), activities: activities.trim() }
            : undefined,
        requestText:
          ticketType === 'request' ? requestText.trim() : ticketType === 'labels' ? labelNamesText.trim() : undefined,
        dueDate: null,
        labelsData:
          ticketType === 'labels' && selectedLabelTypes.length > 0
            ? selectedLabelTypes.map((key) => {
                const found = LABEL_TYPES.find((t) => t.key === key);
                const text =
                  key === 'other' && labelOtherText.trim() ? `Other: ${labelOtherText.trim()}` : found?.label ?? key;
                return { text, color: '#6b7280' };
              })
            : null,
      });
      toast.success('Ticket sent successfully.');
      resetForm();
      setView('list');
      void loadTickets();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not send ticket.');
    } finally {
      setSaving(false);
    }
  };

  if (view === 'list') {
    return (
      <>
        <CommonHeader />
        <Container className="mt--8" fluid>
          <Row>
            <Col xl="10" className="mx-auto">
              <Card className="bg-secondary shadow">
                <CardHeader className="bg-white border-0 d-flex align-items-center justify-content-between">
                  <div>
                    <h3 className="mb-0">Your Submitted Tickets</h3>
                    <small className="text-muted">View and track the status of all your ticket requests</small>
                  </div>
                  <Button color="primary" onClick={() => setView('form')}>
                    Create New Ticket
                  </Button>
                </CardHeader>
                <CardBody>
                  <div className="task-board__ticket-filter-tabs mb-3">
                    {(['all', 'pending', 'completed', 'cancelled'] as FilterTab[]).map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        className={`task-board__ticket-filter-tab${
                          filterTab === tab ? ' task-board__ticket-filter-tab--active' : ''
                        }`}
                        onClick={() => setFilterTab(tab)}
                      >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        {tab !== 'all' && (
                          <span className="task-board__ticket-filter-count">
                            {tickets.filter((t) => t.status === tab).length}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>

                  {loadingTickets ? (
                    <div className="text-center text-muted py-4">Loading tickets...</div>
                  ) : filteredTickets.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      {filterTab === 'all' ? 'No tickets submitted yet.' : `No ${filterTab} tickets.`}
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table task-board__ticket-table">
                        <thead>
                          <tr>
                            <th>Ticket ID</th>
                            <th>Submitted</th>
                            <th>Classroom</th>
                            <th>Category</th>
                            <th>Item Requested</th>
                            <th>Quantity</th>
                            <th>Due Date</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredTickets.map((ticket) => (
                            <tr key={ticket.id}>
                              <td>
                                <strong>{ticket.ticketNumber}</strong>
                              </td>
                              <td>{isoToUs(ticket.date)}</td>
                              <td>{ticket.classroomNumber}</td>
                              <td>{ticketTypeLabel[ticket.ticketType] ?? ticket.ticketType}</td>
                              <td
                                style={{
                                  maxWidth: 180,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {getItemRequested(ticket)}
                              </td>
                              <td>{getQuantity(ticket)}</td>
                              <td>{isoToUs(ticket.dueDate)}</td>
                              <td>
                                <Badge color={STATUS_COLOR[ticket.status] ?? 'secondary'} pill>
                                  {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      <CommonHeader />
      <Container className="mt--8" fluid>
        <Row>
          <Col xl="8" className="mx-auto">
            <Card className="bg-secondary shadow">
              <CardHeader className="bg-white border-0 d-flex align-items-center justify-content-between">
                <h3 className="mb-0">New Teacher Ticket</h3>
                <Button
                  color="secondary"
                  outline
                  size="sm"
                  onClick={() => {
                    resetForm();
                    setView('list');
                  }}
                >
                  ← Back to My Tickets
                </Button>
              </CardHeader>
              <CardBody>
                <Form>
                  <FormGroup>
                    <Label>Your Name</Label>
                    <Input value={teacherName} disabled />
                  </FormGroup>
                  <FormGroup>
                    <Label>Today's Date</Label>
                    <Input value={todayUs()} disabled />
                  </FormGroup>
                  <FormGroup>
                    <Label>Classroom Number</Label>
                    <Input value={classroomNumber} onChange={(e) => setClassroomNumber(e.target.value)} required />
                  </FormGroup>
                  <FormGroup>
                    <Label>Pick One</Label>
                    <Input
                      type="select"
                      value={ticketType}
                      onChange={(e) => setTicketType(e.target.value as TicketType)}
                    >
                      <option value="supplies">Supplies</option>
                      <option value="one_on_one">Meeting</option>
                      <option value="labels">Labels</option>
                      <option value="monthly_curriculum_event">Monthly Curriculum Event</option>
                      <option value="request">Request</option>
                    </Input>
                  </FormGroup>

                  {ticketType === 'supplies' && (
                    <>
                      <FormGroup>
                        <Label>What do you need?</Label>
                        <Input value={whatNeed} onChange={(e) => setWhatNeed(e.target.value)} />
                      </FormGroup>
                      <FormGroup>
                        <Label>How much do you need?</Label>
                        <Input value={howMuchNeed} onChange={(e) => setHowMuchNeed(e.target.value)} />
                      </FormGroup>
                      <FormGroup>
                        <Label>What is it for?</Label>
                        <Input value={whatFor} onChange={(e) => setWhatFor(e.target.value)} />
                      </FormGroup>
                      <FormGroup>
                        <Label>When do you need it by?</Label>
                        <input
                          ref={dateInputRef}
                          className="form-control"
                          style={{ cursor: 'pointer' }}
                          placeholder="MM/DD/YYYY"
                          value={needByDate ? moment(needByDate, 'YYYY-MM-DD').format('MM/DD/YYYY') : ''}
                          onClick={() => {
                            if (dateInputRef.current) {
                              const rect = dateInputRef.current.getBoundingClientRect();
                              setDatePickerStyle({
                                position: 'fixed',
                                bottom: window.innerHeight - rect.top + 4,
                                left: rect.left,
                                zIndex: 99999,
                              });
                            }
                            setDatePickerOpen(true);
                          }}
                        />
                        {datePickerOpen &&
                          createPortal(
                            <>
                              <div
                                style={{ position: 'fixed', inset: 0, zIndex: 99998 }}
                                onClick={() => setDatePickerOpen(false)}
                              />
                              <div style={datePickerStyle}>
                                <ReactDatetime
                                  input={false}
                                  value={needByDate ? moment(needByDate, 'YYYY-MM-DD') : ''}
                                  onChange={(newValue) => {
                                    const m = moment.isMoment(newValue)
                                      ? newValue
                                      : moment(String(newValue), 'MM/DD/YYYY');
                                    setNeedByDate(m.isValid() ? m.format('YYYY-MM-DD') : '');
                                    setDatePickerOpen(false);
                                  }}
                                  timeFormat={false}
                                  dateFormat="MM/DD/YYYY"
                                />
                              </div>
                            </>,
                            document.body,
                          )}
                      </FormGroup>
                    </>
                  )}

                  {ticketType === 'one_on_one' && (
                    <>
                      <FormGroup>
                        <Label>Meeting subject</Label>
                        <Input
                          type="textarea"
                          rows={3}
                          value={oneOnOneNotes}
                          onChange={(e) => setOneOnOneNotes(e.target.value)}
                        />
                      </FormGroup>
                    </>
                  )}

                  {ticketType === 'monthly_curriculum_event' && (
                    <>
                      <FormGroup>
                        <Label>What month is this for?</Label>
                        <Input value={eventMonth} onChange={(e) => setEventMonth(e.target.value)} />
                      </FormGroup>
                      <FormGroup>
                        <Label>What activities are you choosing?</Label>
                        <Input value={activities} onChange={(e) => setActivities(e.target.value)} />
                      </FormGroup>
                    </>
                  )}

                  {ticketType === 'request' && (
                    <FormGroup>
                      <Label>Request</Label>
                      <Input
                        type="textarea"
                        rows={4}
                        value={requestText}
                        onChange={(e) => setRequestText(e.target.value)}
                      />
                    </FormGroup>
                  )}

                  {ticketType === 'labels' && (
                    <>
                      <Card className="mb-3 border">
                        <CardHeader
                          style={{
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            letterSpacing: '0.06em',
                            padding: '0.65rem 1.25rem',
                          }}
                        >
                          LABELS
                        </CardHeader>
                        <CardBody>
                          <p className="mb-3">
                            <strong>Choose Type</strong> <span className="text-danger">*</span>
                          </p>
                          {LABEL_TYPES.map(({ key, label }) => (
                            <div key={key} className="d-flex align-items-center mb-3">
                              <input
                                type="checkbox"
                                id={`label-type-${key}`}
                                checked={selectedLabelTypes.includes(key)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedLabelTypes((prev) => [...prev, key]);
                                  } else {
                                    setSelectedLabelTypes((prev) => prev.filter((t) => t !== key));
                                    if (key === 'other') setLabelOtherText('');
                                  }
                                }}
                                style={{ width: 18, height: 18, marginRight: '0.75rem', cursor: 'pointer' }}
                              />
                              <label
                                htmlFor={`label-type-${key}`}
                                className="mb-0"
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                              >
                                {label}
                              </label>
                              {key === 'other' && (
                                <Input
                                  bsSize="sm"
                                  style={{ marginLeft: '0.5rem', flex: 1, maxWidth: 260 }}
                                  value={labelOtherText}
                                  onChange={(e) => setLabelOtherText(e.target.value)}
                                />
                              )}
                            </div>
                          ))}
                        </CardBody>
                      </Card>

                      <Card className="border">
                        <CardBody>
                          <p className="mb-1">
                            <strong>List Names, Toys or anything you need labels of</strong>{' '}
                            <span className="text-danger">*</span>
                          </p>
                          <p className="text-muted small mb-3">
                            Do not put ALL. Check your AEA App and list the names of the children that you want a cubby
                            label
                          </p>
                          <Input
                            type="textarea"
                            rows={3}
                            placeholder="Your answer"
                            value={labelNamesText}
                            onChange={(e) => setLabelNamesText(e.target.value)}
                          />
                        </CardBody>
                      </Card>
                    </>
                  )}

                  <div className="d-flex justify-content-end mt-4">
                    <Button
                      color="secondary"
                      outline
                      className="mr-2"
                      onClick={() => {
                        resetForm();
                        setView('list');
                      }}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                    <Button color="primary" onClick={() => void handleSubmit()} disabled={saving || !canSubmit}>
                      Send Ticket
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default TeacherTicketPage;
